import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Sparkles } from 'lucide-react';
import { get } from '@/api/client';
import type { OrderSlaResponse, OverviewResponse } from '@/api/bi';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { fmt } from '@/lib/format';
import { rangeFromPreset, rangeISO } from '@/state/filters';
import { cn } from '@/lib/cn';

/**
 * Wall mode — chrome-less, oversized counters tuned for an office TV
 * or a war-room screen. Lives at `/wall` and is intentionally minimal:
 *
 *   - No sidebar, no topbar, no filter chrome.
 *   - Re-polls every 15 seconds (ignoring user `refresh_ms` so the wall
 *     always feels alive even if individual users have polling off).
 *   - Forces dark theme via `data-theme="dark"` while mounted, so the
 *     background looks dramatic regardless of OS.
 *   - Cycles a "fact of the moment" footer every 8 seconds.
 *
 * Anyone with a session and `?wall=1` (or just hitting `/wall`) can
 * project this page — same auth as the rest of the app, no special
 * permission required.
 */
export function WallPage() {
  // Force dark mode while wall is mounted; restore on unmount.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.dataset.theme ?? null;
    html.dataset.theme = 'dark';
    return () => {
      if (prev == null) delete html.dataset.theme;
      else html.dataset.theme = prev;
    };
  }, []);

  const range = useMemo(() => rangeFromPreset('today'), []);
  const iso = useMemo(() => rangeISO(range, 'standard'), [range]);

  const overview = useQuery({
    queryKey: ['wall', 'overview', iso.start, iso.end],
    queryFn: () => get<OverviewResponse>('/api/bi/overview/', { params: { start: iso.start, end: iso.end } }),
    refetchInterval: 15_000,
  });

  const sla = useQuery({
    queryKey: ['wall', 'sla', iso.start, iso.end],
    queryFn: () => get<OrderSlaResponse>('/api/bi/orders/sla/', { params: { start: iso.start, end: iso.end } }),
    refetchInterval: 15_000,
  });

  const data = overview.data;
  const slaData = sla.data;

  const now = useTicker();
  // A wall is read from across the room by people who will not notice a small
  // spinner, so the only two states that matter are "these numbers are current"
  // and "they are not". React Query keeps the last payload on a failed refetch,
  // which is right for the numbers but means a dead backend otherwise looks
  // identical to a quiet trading hour.
  const failing = overview.isError || sla.isError;
  const firstLoad = !data && overview.isLoading;
  const updatedAt = Math.max(overview.dataUpdatedAt, sla.dataUpdatedAt);

  return (
    <div className="min-h-screen w-full bg-background bg-dotgrid-brand text-foreground">
      <div className="mx-auto flex min-h-screen max-w-[1800px] flex-col px-8 py-10">
        <header className="mb-8 flex items-baseline justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary ring-1 ring-inset ring-primary/30">
              <Sparkles className="h-3 w-3" /> Live wall
            </span>
            <p className="font-display text-base font-medium text-muted-foreground">
              {fmt.date(range.from, 'EEEE, d MMMM')} — {fmt.time(now)} (Asia/Kathmandu)
            </p>
          </div>
          <FeedStatus failing={failing} loading={firstLoad} updatedAt={updatedAt} now={now} />
        </header>

        {failing ? (
          <div
            role="alert"
            className="mb-8 flex items-center gap-3 rounded-xl border border-danger/40 bg-danger/10 px-5 py-3"
          >
            <AlertTriangle className="h-5 w-5 shrink-0 text-danger" aria-hidden />
            <p className="text-sm font-medium text-danger">
              Live feed interrupted.{' '}
              <span className="font-normal text-foreground/80">
                {updatedAt
                  ? `Showing the last good read from ${fmt.time(new Date(updatedAt))}. Retrying every 15 seconds.`
                  : 'No data has loaded yet. Retrying every 15 seconds.'}
              </span>
            </p>
          </div>
        ) : null}

        <section className="grid grid-cols-2 gap-8">
          <Big
            label="GMV today"
            value={data?.current.gmv ?? null}
            format={(n) => fmt.npr(n)}
            deltaPct={data?.deltas.gmv_pct ?? null}
            goodWhenUp
            tone="primary"
          />
          <Big
            label="Orders placed"
            value={data?.current.orders_total ?? null}
            format={(n) => fmt.compact(n)}
            deltaPct={data?.deltas.orders_total_pct ?? null}
            goodWhenUp
            tone="info"
          />
        </section>

        <section className="mt-10 grid grid-cols-3 gap-6">
          <Mid
            label="In-flight"
            value={data?.in_flight.total ?? null}
            format={(n) => fmt.number(n)}
            note={`${data?.exceptions ?? 0} exceptions`}
          />
          <Mid
            label="Completed"
            value={data?.current.orders_completed ?? null}
            format={(n) => fmt.number(n)}
            deltaPct={data?.deltas.orders_completed_pct ?? null}
          />
          <Mid
            label="Cancel rate"
            value={data?.current.cancel_rate ?? null}
            format={(n) => fmt.pct(n)}
            deltaPct={data?.deltas.cancel_rate_pct ?? null}
            goodWhenUp={false}
          />
          <Mid
            label="AOV"
            value={data?.current.aov ?? null}
            format={(n) => fmt.npr(n)}
            deltaPct={data?.deltas.aov_pct ?? null}
          />
          <Mid
            label="On-time delivery"
            value={
              slaData && slaData.total_orders > 0
                ? 1 - slaData.total_breaches / slaData.total_orders
                : null
            }
            format={(n) => fmt.pct(n)}
          />
          <Mid
            label="Active users"
            value={data?.active_users ?? null}
            format={(n) => fmt.number(n)}
          />
        </section>

        <footer className="mt-auto pt-10 text-center text-xs uppercase tracking-[0.3em] text-muted-foreground">
          Fasto BI · auto-refresh every 15s · press <kbd className="rounded border border-border bg-muted px-1 py-0.5 font-mono">Esc</kbd> to leave
        </footer>
      </div>
    </div>
  );
}

function Big({
  label,
  value,
  format,
  deltaPct,
  goodWhenUp = true,
  tone = 'primary',
}: {
  label: string;
  value: number | null;
  format: (n: number) => string;
  deltaPct?: number | null;
  goodWhenUp?: boolean;
  tone?: 'primary' | 'info' | 'success';
}) {
  const toneClass =
    tone === 'primary'
      ? 'text-primary'
      : tone === 'info'
        ? 'text-info'
        : 'text-success';
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated/60 p-8 shadow-soft backdrop-blur">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-3 font-display font-bold tracking-tight kpi-num',
          'text-[clamp(3.5rem,7vw,7.5rem)] leading-none',
          toneClass,
        )}
      >
        <AnimatedNumber value={value} format={format} duration={650} />
      </p>
      {deltaPct != null ? (
        <Delta deltaPct={deltaPct} goodWhenUp={goodWhenUp} big />
      ) : null}
    </div>
  );
}

function Mid({
  label,
  value,
  format,
  deltaPct,
  goodWhenUp = true,
  note,
}: {
  label: string;
  value: number | null;
  format: (n: number) => string;
  deltaPct?: number | null;
  goodWhenUp?: boolean;
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-surface/70 p-6 backdrop-blur">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-4xl font-bold tracking-tight kpi-num sm:text-5xl">
        <AnimatedNumber value={value} format={format} duration={500} />
      </p>
      {deltaPct != null ? (
        <Delta deltaPct={deltaPct} goodWhenUp={goodWhenUp} />
      ) : note ? (
        <p className="mt-2 text-xs text-muted-foreground">{note}</p>
      ) : null}
    </div>
  );
}

function Delta({
  deltaPct,
  goodWhenUp,
  big,
}: {
  deltaPct: number;
  goodWhenUp: boolean;
  big?: boolean;
}) {
  const positive = deltaPct > 0;
  const isGood = goodWhenUp ? positive : !positive;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <p
      className={cn(
        'mt-3 inline-flex items-center gap-1 font-medium',
        isGood ? 'text-success' : 'text-danger',
        big ? 'text-base' : 'text-xs',
      )}
    >
      <Icon className={big ? 'h-4 w-4' : 'h-3 w-3'} aria-hidden />
      <span className="kpi-num">{fmt.delta(deltaPct, { unit: 'pct' })}</span>
      <span className="text-muted-foreground">vs prev period</span>
    </p>
  );
}

/**
 * Health of the feed, not of the business.
 *
 * The previous version of this pill said "live" unconditionally — it was pure
 * decoration, with nothing behind it. On a screen nobody interacts with, that
 * is the one label that has to be earned.
 */
function FeedStatus({
  failing,
  loading,
  updatedAt,
  now,
}: {
  failing: boolean;
  loading: boolean;
  updatedAt: number;
  now: Date;
}) {
  if (loading) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted-foreground">
        <span className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground" aria-hidden />
        connecting…
      </span>
    );
  }

  if (failing) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-danger/10 px-3 py-1 text-xs font-medium text-danger">
        <span className="h-2 w-2 animate-pulse rounded-full bg-danger" aria-hidden />
        reconnecting
      </span>
    );
  }

  const secondsOld = updatedAt ? Math.round((now.getTime() - updatedAt) / 1000) : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs text-muted-foreground"
      aria-label="Feed status"
    >
      <span className="live-dot live-dot-primary" aria-hidden />
      live
      {secondsOld != null ? (
        <span className="tabular-nums opacity-70">· {secondsOld}s ago</span>
      ) : null}
    </span>
  );
}

/** Wall-clock time, ticking every second so the header isn't frozen between polls. */
function useTicker(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}
